import { useMemo, type MouseEvent } from "react";
import { useConversations } from "@/api/hooks/use-conversations";
import { useSearchStore } from "../store/search-store";
// Conversation type available from useConversations hook
import { useDebounce } from "@/hooks";
import { useLeftSidebar } from "@/components/ui/sidebar";
import { isMobile } from "@/lib/is-mobile";
import { isModifiedClick } from "@/lib/link-utils";

export const useGlobalSearch = () => {
  const {
    globalSearchQuery,
    globalSearchHistory,
    isGlobalSearchOpen,
    setGlobalSearchQuery,
    setGlobalSearchOpen,
    addToGlobalSearchHistory,
    clearGlobalSearchHistory,
  } = useSearchStore();
  const { setOpen } = useLeftSidebar();

  // Debounce search query for API calls
  const debouncedSearchQuery = useDebounce(globalSearchQuery, 300);

  // API call with search parameter
  const {
    data: conversationsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversations(
    {
      limit: 20,
      sortBy: "-createdAt",
      search: debouncedSearchQuery.trim() || undefined, // API search parametresi kullan
    },
    {
      // Global search açık değilken veya query değişiminde gereksiz istek atmasın
      enabled: isGlobalSearchOpen && !!debouncedSearchQuery.trim(),
    }
  );

  // Process conversations data - API zaten filtreleme yapıyor, client-side filtering gerekmez
  const conversations = useMemo(() => {
    if (!conversationsData?.pages) return [];
    return conversationsData.pages.flatMap((page) => page.data.result);
  }, [conversationsData]);

  // Actions
  const handleSearchQueryChange = (query: string) => {
    setGlobalSearchQuery(query);
  };

  const handleSearchSubmit = (query: string) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery) {
      addToGlobalSearchHistory(trimmedQuery);
    }
  };

  // 🔗 Navigasyonu artık <Link> üstleniyor (bkz. GlobalSearchDialog); burada
  // sadece yan etkiler kalıyor. Arama geçmişine ekleme analytics benzeri —
  // modifier'lı tıklamada (yeni sekme) da çalışsın. Dialog/sidebar kapatma
  // ise sadece mevcut sekmede navigasyon gerçekleşiyorsa yapılmalı.
  const handleConversationSelect = (e: MouseEvent) => {
    // Save search to history if there's a query
    if (globalSearchQuery.trim()) {
      addToGlobalSearchHistory(globalSearchQuery);
    }

    if (isModifiedClick(e)) return; // yeni sekmede açılıyor, dialog/sidebar mevcut sayfada kalmalı

    if (isMobile()) {
      setOpen(false);
    }

    // Close search dialog
    setGlobalSearchOpen(false);
  };

  const handleSelectFromHistory = (query: string) => {
    setGlobalSearchQuery(query);
  };

  const handleClearQuery = () => {
    setGlobalSearchQuery("");
  };

  const handleOpenSearch = () => {
    setGlobalSearchOpen(true);
  };

  const handleCloseSearch = () => {
    setGlobalSearchOpen(false);
    setGlobalSearchQuery("");
  };

  // Return interface
  return {
    // State
    searchQuery: globalSearchQuery,
    searchHistory: globalSearchHistory,
    isSearchOpen: isGlobalSearchOpen,
    conversations, // API'den gelen filtrelenmiş sonuçlar
    isLoading,
    error,

    // Pagination
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Actions
    onSearchQueryChange: handleSearchQueryChange,
    onSearchSubmit: handleSearchSubmit,
    onConversationSelect: handleConversationSelect,
    onSelectFromHistory: handleSelectFromHistory,
    onClearQuery: handleClearQuery,
    onOpenSearch: handleOpenSearch,
    onCloseSearch: handleCloseSearch,
    onClearHistory: clearGlobalSearchHistory,
  };
};
